import "./index.css";
import {
  FloatingFocusManager,
  FloatingNode,
  FloatingPortal,
  useFloatingNodeId,
} from "@floating-ui/react";
import { clsx } from "clsx";
import type { MouseEvent } from "react";
import { useCallback, useMemo } from "react";
import { composeEventHandlers, Slot } from "../../utils";
import { resolveAriaName } from "./aria";
import { DropdownProvider } from "./context";
import type { DropdownContextValue, DropdownProps } from "./types";
import { useViewModel } from "./useViewModel";

export const Dropdown = (props: DropdownProps) => {
  const {
    trigger,
    content,
    floatingFocusManager,
    contentClassName,
    ariaLabel,
    ariaLabelledby,
    preventDefaultOnTrigger,
    stopPropagationOnTrigger,
    innerControl = false,
    scrollable = true,
    preserveTabOrder,
    role = "dialog",
  } = props;

  const {
    refs,
    floatingStyles,
    context,
    getReferenceProps,
    getFloatingProps,
    getItemProps,
    triggerProps,
    controlProps,
    controlId,
    controlLabel,
    navigatesWithArrowKeys,
    open,
    setOpen,
    dismiss,
    placement,
    activeIndex,
    setActiveIndex,
    navigationRef,
    isMounted,
    transitionStyles,
    triggerId,
    contentId,
    returnFocusRef,
  } = useViewModel(props);

  const nodeId = useFloatingNodeId();

  const contextValue = useMemo((): DropdownContextValue => {
    return {
      open,
      setOpen,
      placement,
      floatingStyles,
      refs,
      getReferenceProps,
      getFloatingProps,
      getItemProps,
      controlProps,
      activeIndex,
      setActiveIndex,
      navigationRef,
      context,
      dismiss,
      isMounted,
      transitionStyles,
      triggerId,
      contentId,
      props,
    };
  }, [
    open,
    setOpen,
    placement,
    floatingStyles,
    refs,
    getReferenceProps,
    getFloatingProps,
    getItemProps,
    controlProps,
    activeIndex,
    setActiveIndex,
    navigationRef,
    context,
    dismiss,
    isMounted,
    transitionStyles,
    triggerId,
    contentId,
    props,
  ]);

  const preventEverything = useCallback(
    (e: MouseEvent) => {
      if (stopPropagationOnTrigger) {
        e.stopPropagation();
      }
      if (preventDefaultOnTrigger) {
        e.preventDefault();
      }
    },
    [stopPropagationOnTrigger, preventDefaultOnTrigger],
  );

  const triggerElement = useMemo(() => {
    // useRole gives menu triggers a generated id; ours must win so the panel's
    // aria-labelledby points at it.
    const {
      onClick: referenceOnClick,
      id: _roleId,
      ...otherTriggerProps
    } = triggerProps;

    const composedOnClick = composeEventHandlers(
      referenceOnClick as (e: MouseEvent) => void,
      preventEverything,
    );

    // With an inner control the box only positions and takes pointer events;
    // the control carries id, tab stop and the widget role.
    return (
      <Slot
        ref={refs.setReference}
        id={innerControl ? undefined : controlId}
        tabIndex={innerControl ? -1 : (trigger.props.tabIndex ?? 0)}
        className="nuco-dropdown__trigger-element"
        onClick={composedOnClick}
        {...otherTriggerProps}
      >
        {trigger}
      </Slot>
    );
  }, [
    trigger,
    refs.setReference,
    controlId,
    triggerProps,
    innerControl,
    preventEverything,
  ]);

  const contentElement = useMemo(() => {
    if (!isMounted) return null;

    // useRole labels only menus by their reference, which is the box (not the
    // control) in inner-control mode. Name every panel here instead.
    const { "aria-labelledby": _roleLabelledby, ...floatingProps } =
      getFloatingProps();

    const panelName = resolveAriaName(
      ariaLabel ?? controlLabel,
      ariaLabelledby,
      role === "tooltip" ? undefined : controlId,
    );

    const modal = role === "dialog" && !!floatingFocusManager?.modal;

    const panel = (
      <div
        ref={refs.setFloating}
        style={{ ...floatingStyles, ...transitionStyles }}
        className={clsx(
          "nuco-dropdown__content-element",
          scrollable && "nuco-dropdown__content-element--scrollable",
          contentClassName,
        )}
        aria-modal={modal ? "true" : undefined}
        {...floatingProps}
        {...panelName}
        id={contentId}
        role={role}
      >
        {content}
      </div>
    );

    // A tooltip never takes focus, so it gets no focus manager and no focus guards.
    if (role === "tooltip") return panel;

    return (
      <FloatingFocusManager
        context={context}
        {...floatingFocusManager}
        modal={modal}
        returnFocus={returnFocusRef}
      >
        {panel}
      </FloatingFocusManager>
    );
  }, [
    isMounted,
    getFloatingProps,
    ariaLabel,
    ariaLabelledby,
    role,
    controlId,
    controlLabel,
    floatingFocusManager,
    refs.setFloating,
    floatingStyles,
    transitionStyles,
    scrollable,
    contentClassName,
    contentId,
    content,
    context,
    returnFocusRef,
  ]);

  return (
    <DropdownProvider value={contextValue}>
      <FloatingNode id={nodeId}>
        {triggerElement}
        <FloatingPortal
          preserveTabOrder={preserveTabOrder ?? !navigatesWithArrowKeys}
        >
          {contentElement}
        </FloatingPortal>
      </FloatingNode>
    </DropdownProvider>
  );
};
